import React from 'react'
import MediaQuery from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import {
  Card as AcornsCard,
  ConfirmationModal as AcornsConfirmationModal,
  ModalAction,
  ModalActionTypes,
  theme,
} from '@acorns/web-components'
import { StyledRemoteString, useRemoteStrings } from '@acorns/web-utils'
import styled from 'styled-components'

import withdrawLogo from 'assets/images/withdraw-zero-balance.png'
import ConfirmationModal from 'components/ConfirmationModal'
import useWindowDimensions from 'hooks/useWindowDimensions'
import themeValues, { style } from 'theme'
import { USDFormatter } from 'utils/number/currency'

import {
  RedStyledCloseAccountButton,
  StyledBody,
  StyledCard,
  StyledImage,
} from '../components'
import { MergedProps, ModalProps } from './index'

const StyledRedButton = styled(RedStyledCloseAccountButton)`
  width: auto;
  @media (max-width: ${style((theme) => theme.breakpoints.sm - 1)}px) {
    width: 100% !important;
    margin-top: auto;
  }
`
const Card = styled(StyledCard)<{ innerHeight?: number }>`
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    height: ${(props) => `calc(${props.innerHeight}px - 78px)`};
    display: flex;
    justify-content: space-between;
  }
`

const WVCard = styled(StyledCard)<{ innerHeight?: number }>`
  height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
  &&& {
    border-radius: 0;
  }
`
const BodyContainer = styled.div`
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    height: auto;
    padding-top: 50px;
  }
  @media screen and (max-width: 375px) {
    padding-top: 10px;
  }
`

const ProcessingWithdrawls = styled.div`
  max-width: 500px;
  min-width: 400px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    min-width: 200px;
  }
  margin: 0 auto;
`

const Item = styled.div`
  border: 1px solid ${style((theme) => theme.colors.ivory)};
  &:first-child {
    border-top-left-radius: ${style((theme) => theme.borderRadius.large)};
    border-top-right-radius: ${style((theme) => theme.borderRadius.large)};
  }
  &:not(:first-child):not(:last-child) {
    border-top: none;
  }
  &:last-child:not(:first-child) {
    border-top: none;
    border-bottom-left-radius: ${style((theme) => theme.borderRadius.large)};
    border-bottom-right-radius: ${style((theme) => theme.borderRadius.large)};
  }
  &:last-child:first-child {
    border-bottom-left-radius: ${style((theme) => theme.borderRadius.large)};
    border-bottom-right-radius: ${style((theme) => theme.borderRadius.large)};
  }
  margin: 0 auto;
  height: 60px;
  padding: 10px 20px;
  align-items: center;
  justify-content: space-between;
  display: flex;
`

const StyledIcon = styled.div`
  float: left;
  margin-top: 4px;
  max-width: 24px;
  max-height: 24px;
`
const Product = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-top: 5px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 22px;
  color: ${style((theme) => theme.colors.slate)};
  margin-left: 12px;
`

const Amount = styled.span`
  margin-top: 5px;
  font-size: 14px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 22px;
  text-align: right;
  color: ${style((theme) => theme.colors.slate)};
  float: right;
`
const WrappingDiv = styled.div`
  display: inline-block;
  align-items: flex-start;
`
const ButtonContainer = styled.div`
  border-top: 1px solid ${style((theme) => theme.colors.ivory)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-top: none;
    position: relative;
    width: 100%;
  }
`

const Footnote = styled.div`
  font-size: 12px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 16px;
  text-align: center;
  margin-bottom: 50px;
  margin-top: 30px;
  color: ${style((theme) => theme.colors.stone)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding-top: 20px;
    margin-top: 0;
    margin-bottom: 20px;
  }
`

const DisclaimerCard = styled(AcornsCard)`
  background-color: ${theme.colors.ivoryLight};
  border: none;
  box-shadow: none;
  margin: 0 auto 30px auto;
  max-width: 500px;
  padding: 10px;

  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding: 5px 10px;
    margin: 20px auto 0 auto;
  }
`

const DisclaimerCardText = styled.p`
  color: ${theme.colors.slate};
  font-size: 14px;
  line-height: 22px;
`

const renderBalances = (balances: { product: string; amount: number }[]) => {
  return balances.map((item) => (
    <Item key={item.product}>
      <WrappingDiv>
        <StyledIcon>
          <AcornsIcon
            icon={AcornsIcon.Icon[item.product as keyof typeof AcornsIcon.Icon]}
            width="24px"
          />
        </StyledIcon>
        <Product>
          {item.product === 'Spend' ? 'Checking' : item.product}
        </Product>
      </WrappingDiv>

      <Amount data-testid={`${item.product}_balance`}>
        {USDFormatter.format(item.amount)}
      </Amount>
    </Item>
  ))
}
type RequestScreenProps = {
  confirmationModalProps: ModalProps
  hasPendingTransactions: boolean
  hasNoBankLinked: boolean
}
type Props = Pick<
  MergedProps,
  | 'handleShowConfirmAccountClosureModal'
  | 'shouldShowCloseAccountConfirmationModal'
  | 'setShouldShowCloseAccountConfirmationModal'
  | 'handleShowNoBankLinkedModal'
  | 'handleNoBankLinkedModalCancel'
  | 'setShouldShowNoBankLinkedModal'
  | 'shouldShowNoBankLinkedModal'
  | 'handleShowPendingTransactionModal'
  | 'handlePendingTransactionModalCancel'
  | 'shouldShowPendingTransactionModal'
  | 'setShouldShowPendingTransactionModal'
  | 'userData'
  | 'loading'
  | 'balances'
  | 'hasBalance'
  | 'hasSelfDirectedInvestPortfolio'
  | 'isWebView'
> &
  RequestScreenProps

export const RequestScreen: React.FC<Props> = ({
  confirmationModalProps,
  handleShowConfirmAccountClosureModal,
  shouldShowCloseAccountConfirmationModal,
  setShouldShowCloseAccountConfirmationModal,
  handleShowPendingTransactionModal,
  handlePendingTransactionModalCancel,
  shouldShowPendingTransactionModal,
  setShouldShowPendingTransactionModal,
  handleShowNoBankLinkedModal,
  handleNoBankLinkedModalCancel,
  setShouldShowNoBankLinkedModal,
  shouldShowNoBankLinkedModal,
  userData,
  loading,
  balances,
  hasBalance,
  hasPendingTransactions,
  hasNoBankLinked,
  hasSelfDirectedInvestPortfolio,
  isWebView,
}) => {
  const CardComponent = isWebView ? WVCard : Card
  const { getRemoteString } = useRemoteStrings()
  const { height } = useWindowDimensions()

  const handleShowModal = () => {
    if (hasPendingTransactions) {
      handleShowPendingTransactionModal()
    } else if (hasNoBankLinked) {
      handleShowNoBankLinkedModal()
    } else {
      handleShowConfirmAccountClosureModal()
    }
  }

  const noBankLinkedModalActions: ModalAction[] = [
    {
      handler: handleNoBankLinkedModalCancel,
      label: getRemoteString({
        key: 'cancel_subscription.modal.no_bank_linked.cta',
      }),
      type: ModalActionTypes.secondary,
    },
  ]

  const noBankLinkedModalBody = getRemoteString({
    key: 'cancel_subscription.modal.no_bank_linked.body',
  })

  const pendingModalActions: ModalAction[] = [
    {
      handler: handlePendingTransactionModalCancel,
      label: getRemoteString({
        key: 'passions.cancel_subscription.modal.pending_transaction.cta',
      }),
      type: ModalActionTypes.secondary,
    },
  ]
  const pendingModalBody = (
    <StyledRemoteString
      stringKey={'passions.cancel_subscription.modal.pending_transaction.body'}
      wrappers={[
        {
          component: styled.a``,
          props: { href: 'mailto:support@acorns.com' },
        },
      ]}
    />
  )
  const pendingModalHeader = getRemoteString({
    key: 'passions.cancel_subscription.modal.pending_transaction.header',
  })

  return (
    <>
      {hasBalance && (
        <CardComponent
          isWebView={isWebView}
          innerHeight={isWebView ? height : undefined}
        >
          <BodyContainer>
            <StyledImage src={withdrawLogo} />

            <StyledBody>
              Your money will be transferred to your{' '}
              <b>
                {userData?.fundingSource?.name} {userData?.fundingSource?.last4}
              </b>{' '}
              account, then we’ll automatically close your accounts and cancel
              your subscription.
            </StyledBody>
            <ProcessingWithdrawls>
              {balances && renderBalances(balances)}
            </ProcessingWithdrawls>
            <MediaQuery minWidth={themeValues.breakpoints.sm}>
              <Footnote>
                Transfers may take up to 3-6 business days to settle after your
                shares have been sold.
              </Footnote>
            </MediaQuery>
            {hasSelfDirectedInvestPortfolio && (
              <DisclaimerCard>
                <DisclaimerCardText>
                  {getRemoteString({
                    key: 'settings.subscription.close_account_summary.note.self_directed',
                  })}
                </DisclaimerCardText>
              </DisclaimerCard>
            )}
          </BodyContainer>
          <ButtonContainer>
            <MediaQuery maxWidth={themeValues.breakpoints.sm - 1}>
              <Footnote>
                Transfers may take up to 3-6 business days to settle after your
                shares have been sold.
              </Footnote>
            </MediaQuery>
            <StyledRedButton onPress={handleShowModal} disabled={loading}>
              Withdraw and request closure
            </StyledRedButton>
          </ButtonContainer>
        </CardComponent>
      )}

      {!hasBalance && (
        <>
          <CardComponent
            isWebView={isWebView}
            innerHeight={isWebView ? height : undefined}
          >
            <BodyContainer>
              <StyledImage src={withdrawLogo} />
              <StyledBody>
                If you cancel your subscription, you’ll miss out on Round-Ups,
                our Found Money partners and your own personalized investment
                portfolio.
              </StyledBody>
              {hasSelfDirectedInvestPortfolio && (
                <DisclaimerCard>
                  <DisclaimerCardText>
                    {getRemoteString({
                      key: 'settings.subscription.close_account_summary.note.self_directed',
                    })}
                  </DisclaimerCardText>
                </DisclaimerCard>
              )}
            </BodyContainer>

            <StyledRedButton onPress={handleShowModal} disabled={loading}>
              Cancel subscription
            </StyledRedButton>
          </CardComponent>
        </>
      )}

      {shouldShowNoBankLinkedModal && (
        <AcornsConfirmationModal
          // @ts-ignore - `props.modalActions` uses `ModalAction` type from web-components, but this `ConfirmationModal` type is from /src/components instead of web-components; type error is due to prop typing for `href`, but  it's unused here
          actions={noBankLinkedModalActions}
          image={<AcornsIcon icon={AcornsIcon.Icon.Alert} width="104px" />}
          message={noBankLinkedModalBody}
          isOpen={shouldShowNoBankLinkedModal}
          onRequestClose={() =>
            setShouldShowNoBankLinkedModal(!shouldShowNoBankLinkedModal)
          }
        />
      )}

      {shouldShowPendingTransactionModal && (
        <AcornsConfirmationModal
          // @ts-ignore - `props.modalActions` uses `ModalAction` type from web-components, but this `ConfirmationModal` type is from /src/components instead of web-components; type error is due to prop typing for `href`, but  it's unused here
          actions={pendingModalActions}
          image={<AcornsIcon icon={AcornsIcon.Icon.Alert} width="104px" />}
          message={pendingModalBody}
          title={pendingModalHeader}
          isOpen={shouldShowPendingTransactionModal}
          onRequestClose={() =>
            setShouldShowPendingTransactionModal(
              !shouldShowPendingTransactionModal,
            )
          }
        />
      )}

      {shouldShowCloseAccountConfirmationModal && hasBalance && (
        <ConfirmationModal
          // @ts-ignore - `props.modalActions` uses `ModalAction` type from web-components, but this `ConfirmationModal` type is from /src/components instead of web-components; type error is due to prop typing for `href`, but  it's unused here
          actions={confirmationModalProps.modalActions}
          body={confirmationModalProps.modalBody}
          header={confirmationModalProps.modalHeader}
          onRequestClose={() =>
            setShouldShowCloseAccountConfirmationModal(
              !shouldShowCloseAccountConfirmationModal,
            )
          }
          isOpen={shouldShowCloseAccountConfirmationModal}
        />
      )}

      {shouldShowCloseAccountConfirmationModal && !hasBalance && (
        <ConfirmationModal
          // @ts-ignore - `props.modalActions` uses `ModalAction` type from web-components, but this `ConfirmationModal` type is from /src/components instead of web-components; type error is due to prop typing for `href`, but  it's unused here
          actions={confirmationModalProps.modalActions}
          body={confirmationModalProps.modalBody}
          header={confirmationModalProps.modalHeader}
          onRequestClose={() =>
            setShouldShowCloseAccountConfirmationModal(
              !shouldShowCloseAccountConfirmationModal,
            )
          }
          isOpen={shouldShowCloseAccountConfirmationModal}
        />
      )}
    </>
  )
}
